import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useSearchParams } from "react-router-dom";

export function useFilters({
  selector,
  action,
  resetAction,
  arrayFields = [],
}) {
  const dispatch = useDispatch();
  const reduxFilters = useSelector(selector, shallowEqual);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialized = useRef(false);

  // Initialize Redux from URL on mount AND when returning to page
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const initialFilters = {};
    let hasParams = false;

    for (const [key, value] of searchParams.entries()) {
      if (!value || value === "null") continue;
      hasParams = true;
      let val = value.includes(",") ? value.split(",") : value;
      if (arrayFields.includes(key)) val = Array.isArray(val) ? val : [val];
      initialFilters[key] = val;
    }

    // If URL has params, sync them to Redux
    if (hasParams) {
      dispatch(action(initialFilters));
    } else {
      // If URL is empty, clear Redux state
      dispatch(resetAction());
    }
  }, [dispatch, action, resetAction, searchParams, arrayFields]);

  // Build URL params (skip null / empty arrays)
  const buildUrlParams = useCallback((filters) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, val]) => {
      if (val === undefined || val === null) return;
      if (Array.isArray(val) && val.length === 0) return;
      params.set(key, Array.isArray(val) ? val.join(",") : val);
    });
    return params;
  }, []);

  // Set multiple filters at once
  const setFilters = useCallback(
    (newFiltersOrFn) => {
      dispatch((_, getState) => {
        const currentFilters = selector(getState());
        const mergedFilters =
          typeof newFiltersOrFn === "function"
            ? newFiltersOrFn(currentFilters)
            : { ...currentFilters, ...newFiltersOrFn };

        dispatch(action(mergedFilters));
        setSearchParams(buildUrlParams(mergedFilters), { replace: true });
      });
    },
    [dispatch, action, selector, buildUrlParams, setSearchParams]
  );

  // Clear single filter
  const clearOne = useCallback(
    (key) => {
      const val = arrayFields.includes(key) ? [] : null;
      const updatedFilters = { ...reduxFilters, [key]: val };
      dispatch(action({ [key]: val }));
      setSearchParams(buildUrlParams(updatedFilters), { replace: true });
    },
    [
      reduxFilters,
      dispatch,
      action,
      arrayFields,
      buildUrlParams,
      setSearchParams,
    ]
  );

  // Clear all filters
  const clearAll = useCallback(() => {
    dispatch(resetAction());
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [dispatch, resetAction, setSearchParams]);

  return { filters: reduxFilters, setFilters, clearOne, clearAll };
}